import React, { useEffect, useState, useRef, memo } from 'react';
import { CheckCircle, XCircle, Map, Home, Plane } from 'lucide-react';
export function SalesLetter() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);
  return <article ref={sectionRef} className="max-w-4xl mx-auto px-4 py-12 text-lg text-slate-700 leading-relaxed bg-white" data-id="element-268">
      {/* Pregunta gancho */}
      <div className={`text-center mb-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-269">
        <p className="text-2xl md:text-3xl font-black text-slate-900 leading-tight" data-id="element-270">
          ¿Entrás al aula <span className="text-red-600" data-id="element-271">sin estar seguro</span>{' '}
          de cómo enseñar SIC con claridad?
        </p>
        <p className="mt-4 text-xl text-slate-600" data-id="element-272">
          El problema{' '}
          <span className="font-bold text-slate-900" data-id="element-273">no sos vos</span>. Es que{' '}
          <span className="font-bold text-red-600" data-id="element-274">
            nadie te dio una herramienta
          </span>{' '}
          pensada para tu realidad docente.
        </p>
      </div>

      {/* Dos columnas: realidad vs aula */}
      <div className={`grid md:grid-cols-2 gap-5 mb-14 transition-all duration-700 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-275">
        <div className="bg-green-50 rounded-2xl p-6 border-2 border-green-200" data-id="element-276">
          <h3 className="font-bold text-green-800 text-lg mb-4 flex items-center gap-2" data-id="element-277">
            <CheckCircle className="w-5 h-5 flex-shrink-0" data-id="element-278" />
            La contabilidad está en todos lados
          </h3>
          <ul className="space-y-3" data-id="element-279">
            {['En el precio del colectivo', 'En el sueldo que no alcanza', 'En las decisiones diarias'].map(item => <li key={item} className="flex items-start gap-2 text-slate-700" data-id="element-280">
                <span className="text-green-600 font-bold mt-0.5" data-id="element-281">✓</span>
                <span data-id="element-282">{item}</span>
              </li>)}
          </ul>
        </div>

        <div className="bg-red-50 rounded-2xl p-6 border-2 border-red-200" data-id="element-283">
          <h3 className="font-bold text-red-800 text-lg mb-4 flex items-center gap-2" data-id="element-284">
            <XCircle className="w-5 h-5 flex-shrink-0" data-id="element-285" />
            Pero en el aula pasa esto
          </h3>
          <ul className="space-y-3" data-id="element-286">
            {['Alumnos desconectados', 'Miradas perdidas', 'Celulares bajo el banco'].map(item => <li key={item} className="flex items-start gap-2" data-id="element-287">
                <span className="text-red-600 font-bold mt-0.5" data-id="element-288">✗</span>
                <span className="font-semibold text-red-700" data-id="element-289">{item}</span>
              </li>)}
          </ul>
          <div className="mt-5 pt-4 border-t border-red-200 text-center" data-id="element-290">
            <p className="font-bold text-red-700 italic" data-id="element-291">
              "Profe… ¿esto para qué sirve?"
            </p>
          </div>
        </div>
      </div>

      {/* The Solution: SIC 360 */}
      <div className="mb-12" data-id="element-292">
        <div className="text-center mb-10" data-id="element-293">
          <h2 className="text-5xl font-black text-slate-900 mb-4" data-id="element-294">
            Y ahí aparece <span className="text-orange-500" data-id="element-295">SIC 360</span>
          </h2>
        </div>

        <p className="mb-10 font-bold text-2xl text-slate-900" data-id="element-296">
          Es un{' '}
          <span className="text-orange-500" data-id="element-297">sistema de clases listas</span>,
          pensado específicamente para docentes de secundaria.
        </p>

        <div className="bg-white shadow-xl rounded-2xl p-10 border-2 border-slate-200 mb-10" data-id="element-298">
          <h4 className="font-bold text-xl mb-8 text-center text-slate-900" data-id="element-299">
            Un enfoque circular y progresivo que{' '}
            <span className="text-orange-500" data-id="element-300">cambia la lógica:</span>
          </h4>
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 text-center mb-8" data-id="element-301">
            <div className="flex-1" data-id="element-302">
              <p className="text-sm text-slate-500 uppercase tracking-wide mb-2" data-id="element-303">
                Del
              </p>
              <p className="font-bold text-blue-600 text-2xl" data-id="element-304">PARA QUÉ</p>
            </div>
            <div className="text-slate-300 text-2xl" data-id="element-305">→</div>
            <div className="flex-1" data-id="element-306">
              <p className="text-sm text-slate-500 uppercase tracking-wide mb-2" data-id="element-307">
                Al
              </p>
              <p className="font-bold text-blue-600 text-2xl" data-id="element-308">CÓMO</p>
            </div>
            <div className="text-slate-300 text-2xl" data-id="element-309">→</div>
            <div className="flex-1" data-id="element-310">
              <p className="text-sm text-slate-500 uppercase tracking-wide mb-2" data-id="element-311">
                Al
              </p>
              <p className="font-bold text-blue-600 text-2xl" data-id="element-312">QUÉ</p>
            </div>
          </div>
          <div className="h-px bg-slate-200 my-8" data-id="element-313"></div>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-314">
            <div className="flex items-center gap-3" data-id="element-315">
              <CheckCircle className="text-green-500 w-6 h-6 flex-shrink-0" data-id="element-316" />
              <span className="text-lg" data-id="element-317">
                De lo <span className="font-bold" data-id="element-318">concreto</span> a lo
                conceptual
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-319">
              <CheckCircle className="text-green-500 w-6 h-6 flex-shrink-0" data-id="element-320" />
              <span className="text-lg" data-id="element-321">
                De la <span className="font-bold" data-id="element-322">lógica del sistema</span> a la
                técnica
              </span>
            </div>
          </div>
          <div className="mt-8 text-center font-bold text-blue-600 text-xl" data-id="element-323">
            No memorizar. 👉{' '}
            <span className="text-orange-500" data-id="element-324">
              Entender el sistema completo.
            </span>
          </div>
        </div>
      </div>

      {/* Analogies */}
      <div className="mb-12" data-id="element-325">
        <h3 className="text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-326">
          Pensalo así
        </h3>
        <div className="grid gap-6" data-id="element-327">
          <div className="flex items-start gap-6 p-6 bg-white border-2 border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow" data-id="element-328">
            <div className="bg-orange-100 p-4 rounded-xl" data-id="element-329">
              <Map className="w-8 h-8 text-orange-600" data-id="element-330" />
            </div>
            <div data-id="element-331">
              <h4 className="font-bold text-xl mb-2" data-id="element-332">Un GPS educativo</h4>
              <p className="text-slate-600 text-lg" data-id="element-333">
                Sabés el recorrido.{' '}
                <span className="font-bold text-orange-500" data-id="element-334">
                  No improvisás.
                </span>
              </p>
            </div>
          </div>
          <div className="flex items-start gap-6 p-6 bg-white border-2 border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow" data-id="element-335">
            <div className="bg-blue-100 p-4 rounded-xl" data-id="element-336">
              <Home className="w-8 h-8 text-blue-600" data-id="element-337" />
            </div>
            <div data-id="element-338">
              <h4 className="font-bold text-xl mb-2" data-id="element-339">Un plano de una casa</h4>
              <p className="text-slate-600 text-lg" data-id="element-340">
                Primero <span className="font-bold" data-id="element-341">cimientos</span>. Después
                estructura.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-6 p-6 bg-white border-2 border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow" data-id="element-342">
            <div className="bg-indigo-100 p-4 rounded-xl" data-id="element-343">
              <Plane className="w-8 h-8 text-indigo-600" data-id="element-344" />
            </div>
            <div data-id="element-345">
              <h4 className="font-bold text-xl mb-2" data-id="element-346">Un manual de piloto</h4>
              <p className="text-slate-600 text-lg" data-id="element-347">
                Podés volar igual… pero con esto{' '}
                <span className="font-bold text-orange-500" data-id="element-348">
                  todo es más seguro.
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Origin Story */}
      <div className="bg-slate-50 p-10 rounded-2xl border-2 border-slate-200" data-id="element-349">
        <h3 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-350">
          SIC 360 nació de una{' '}
          <span className="text-orange-500" data-id="element-351">frustración real</span>
        </h3>
        <p className="mb-4 text-slate-700" data-id="element-352">
          De ver docentes preparados, con vocación, agotados. De clases que no
          funcionaban. De programas imposibles de bajar a tierra. De alumnos
          desconectados.
        </p>
        <p className="mb-4 font-bold text-slate-900 text-xl" data-id="element-353">
          La pregunta fue simple: ¿Y si el problema no es el docente…{' '}
          <span className="text-orange-500" data-id="element-354">sino el material?</span>
        </p>
        <p className="text-slate-700 text-lg" data-id="element-355">
          Así nació SIC 360. No como contenido. 👉{' '}
          <span className="font-bold text-blue-600" data-id="element-356">
            Como solución pedagógica.
          </span>
        </p>
      </div>
    </article>;
}